import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { useMemo, useEffect, type ReactNode } from 'react';

import { useHeaderTitle, useNavigationHeader, useSelectedPharmacy } from 'app/hooks';
import { Tabs } from 'app/shared-components/structure';

import useServiceContext from '../hooks/useServiceContext';
import type { CanadaService } from '../types';

// Canadian fork of layouts/PharmacyServiceDetailsLayout: no Payment tab.
const tabs = (serviceId: string | undefined, service: CanadaService | undefined) => [
  {
    label: 'Settings',
    value: 'settings',
    path: `/services/${serviceId}/settings`,
  },
  ...(service?.type !== 'Consultation'
    ? [
        {
          label: 'Content',
          value: 'content',
          path: `/services/${serviceId}/content`,
        },
      ]
    : []),
  {
    label: 'Schedule',
    value: 'schedule',
    path: `/services/${serviceId}/schedule`,
  },
];

interface ServiceDetailsLayoutProps {
  children: ReactNode;
}

export default function ServiceDetailsLayout({ children }: ServiceDetailsLayoutProps): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { serviceId } = useParams();

  const { service } = useServiceContext();
  const selectedPharmacy = useSelectedPharmacy();

  const processedTabs = useMemo(() => tabs(serviceId, service), [serviceId, service]);

  const selectedTab = useMemo(() => {
    return processedTabs.find((tab) => location.pathname.endsWith(tab.value))?.value;
  }, [location.pathname, processedTabs]);

  useEffect(() => {
    // If current URL points to a tab that is not available (e.g. Payment, removed
    // for Canada), normalise to the first available tab.
    const urlMatchesTab = processedTabs.some((tab) => location.pathname.endsWith(tab.value));
    if (!urlMatchesTab) navigate(processedTabs[0].path, { replace: true, relative: 'path' });
  }, [location.pathname, processedTabs, navigate]);

  useNavigationHeader({ title: 'Back to Services', navigateTo: '/services' });

  function handleTabChange(_event: unknown, newValue: string): void {
    const newPath = processedTabs.find((tab) => tab.value === newValue)?.path;
    if (newPath) navigate(newPath, { replace: true });
  }

  const pageTitle = useMemo(() => {
    if (!service?.name) return null;

    const serviceName = service?.customName || service?.name;
    const pharmacyName = selectedPharmacy?.pharmacy_name
      ? `(${selectedPharmacy?.pharmacy_name})`
      : null;

    return [serviceName, pharmacyName].filter(Boolean).join(' ');
  }, [service?.name, service?.customName, selectedPharmacy?.pharmacy_name]);

  const isServiceDisabled = !(!service || service.isEnabled);

  useHeaderTitle(pageTitle, isServiceDisabled);

  return (
    <Box className="grow flex flex-col px-20 py-16 gap-16 bg-white overflow-hidden">
      <Tabs tabs={processedTabs} selectedTab={selectedTab ?? ''} handleChange={handleTabChange} />
      {children}
    </Box>
  );
}
